import { useEffect, useState, type ReactNode } from "react";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { fetchMahnungPdf } from "@/lib/mahnwesen";
import { TimesheetPdfEmbed } from "@/features/timesheetFlow/components/TimesheetPdfEmbed";
import {
  documentChainStepPdfVariant,
  formatDocumentChainStepLabel,
  type DocumentChain,
  type DocumentChainStep,
} from "@/lib/dokumente";
import { DocumentPdfEmbed } from "@/pages/dokumente/DocumentPdfEmbed";

/** Tab-Label inkl. Badge nur für das neueste Angebot. */
export function documentChainStepTabLabel(step: DocumentChainStep): ReactNode {
  const text = formatDocumentChainStepLabel(step);
  if (!step.is_latest || step.kind !== "angebot") return text;
  return (
    <>
      <span>{text}</span>
      <span className="eh-segmented-item__badge">aktuell</span>
    </>
  );
}

interface DocumentChainPanelProps {
  chain: DocumentChain;
  activeKey: string;
  onTabChange: (key: string) => void;
  currentDocId?: number | null;
  onOpenDocument?: (docId: number) => void;
  onDeleteDraft?: (docId: number) => void;
  className?: string;
  /** Für Druck-Dialog über dem Editor-Dialog. */
  zIndexClass?: string;
}

export function DocumentChainPanel({
  chain,
  activeKey,
  onTabChange,
  currentDocId = null,
  onOpenDocument,
  onDeleteDraft,
  className,
  zIndexClass,
}: DocumentChainPanelProps) {
  const activeStep = chain.steps.find((step) => step.key === activeKey) ?? chain.steps[0] ?? null;
  const tabOptions = chain.steps.map((step) => ({
    value: step.key,
    label: documentChainStepTabLabel(step),
  }));

  function handleTabChange(key: string) {
    const step = chain.steps.find((item) => item.key === key);
    onTabChange(key);
    // Beim Wechsel auf ein anderes Geschäftsdokument den Editor darauf umschalten,
    // damit Aktionsleiste zur Ansicht passt (Angebot, Rechnung/Storno, AB, Lieferschein).
    const openEditorKinds = new Set([
      "angebot",
      "rechnung",
      "auftragsbestaetigung",
      "lieferschein",
    ]);
    if (
      step &&
      openEditorKinds.has(step.kind) &&
      step.doc_id != null &&
      currentDocId != null &&
      step.doc_id !== currentDocId &&
      onOpenDocument
    ) {
      onOpenDocument(step.doc_id);
    }
  }

  return (
    <div className={className}>
      {tabOptions.length > 1 ? (
        <div className="mb-4 overflow-x-auto">
          <SegmentedControl
            value={activeKey}
            onChange={handleTabChange}
            options={tabOptions}
            className="min-w-max"
          />
        </div>
      ) : null}

      {activeStep?.kind === "kalkulation" ? (
        <Alert variant="info" className="mb-4">
          Internes Kalkulationsdokument mit Material, Lohn und internen Notizen — nicht für Kunden
          bestimmt.
        </Alert>
      ) : null}
      {activeStep?.kind === "beauftragung" ? (
        <Alert variant="info" className="mb-4">
          Hochgeladenes Beauftragungsdokument aus dem Projektordner — verknüpft mit dem Angebot.
        </Alert>
      ) : null}

      {activeStep?.kind === "mahnung" && activeStep.mahnung_id ? (
        <MahnungChainPdf mahnungId={activeStep.mahnung_id} title={formatDocumentChainStepLabel(activeStep)} />
      ) : null}

      {activeStep?.kind === "stundennachweis" && activeStep.timesheet_id ? (
        <TimesheetPdfEmbed
          timesheetId={activeStep.timesheet_id}
          isSigned={Boolean(activeStep.timesheet_signed)}
          bare
          className="min-h-[min(72vh,56rem)] rounded-xl border border-separator"
        />
      ) : null}

      {activeStep?.doc_id && activeStep.kind !== "stundennachweis" ? (
        activeStep.status === "entwurf" && activeStep.doc_id !== currentDocId ? (
          <div className="flex min-h-[min(40vh,24rem)] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-separator bg-fill-quaternary/20 px-6 py-10 text-center">
            <p className="text-sm text-label-secondary">
              {formatDocumentChainStepLabel(activeStep)} ist noch ein Entwurf — PDF und Bearbeitung im
              Editor.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {onOpenDocument ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onOpenDocument(activeStep.doc_id!)}
                >
                  Öffnen
                </Button>
              ) : null}
              {onDeleteDraft ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => onDeleteDraft(activeStep.doc_id!)}
                >
                  Entwurf löschen
                </Button>
              ) : null}
            </div>
          </div>
        ) : activeStep.status === "storniert" ? (
          <div className="flex min-h-[min(40vh,24rem)] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-separator bg-fill-quaternary/20 px-6 py-10 text-center">
            <p className="text-sm text-label-secondary">
              {formatDocumentChainStepLabel(activeStep)} wurde storniert und hat kein PDF. Über die
              Kette zur Stornorechnung oder zu den anderen Dokumenten wechseln.
            </p>
            {onOpenDocument && activeStep.doc_id != null && activeStep.doc_id !== currentDocId ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenDocument(activeStep.doc_id!)}
              >
                Öffnen
              </Button>
            ) : null}
          </div>
        ) : (
          <DocumentPdfEmbed
            docId={activeStep.doc_id}
            variant={documentChainStepPdfVariant(activeStep)}
            zIndexClass={zIndexClass}
          />
        )
      ) : null}
    </div>
  );
}

function MahnungChainPdf({ mahnungId, title }: { mahnungId: number; title: string }) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlob(null);
    setError(null);
    void fetchMahnungPdf(mahnungId)
      .then((next) => {
        if (!cancelled) setBlob(next.type === "application/pdf" ? next : new Blob([next], { type: "application/pdf" }));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Mahnung konnte nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, [mahnungId]);

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }
  if (!blob) {
    return (
      <div className="flex min-h-[min(40vh,24rem)] items-center justify-center gap-2 text-sm text-label-secondary">
        <Spinner /> Mahnung wird geladen …
      </div>
    );
  }
  return (
    <InlinePdfView
      blob={blob}
      title={title}
      className="min-h-[min(72vh,56rem)] rounded-xl border border-separator"
    />
  );
}

export function defaultChainTabKey(chain: DocumentChain, preferBeauftragung = false): string {
  if (preferBeauftragung) {
    const beauftragung = chain.steps.find((step) => step.kind === "beauftragung");
    if (beauftragung) return beauftragung.key;
  }
  return chain.current_key || chain.steps[0]?.key || "document";
}
